// lib/pipeline-watch/track-drug.ts
//
// "Track this drug" composer (Trial Tracker Week 3). One drug at a time
// lands in the member's default watchlist — a watched_landscapes row with
// kind='watchlist', name "My tracked drugs", at most one per user (partial
// unique index from migration 0066 makes the find-or-create race-safe:
// a concurrent duplicate insert 23505s and we re-select the winner).
//
// The first successful track action also stamps tracker_access
// (trial_started_at) via the SERVICE client: authenticated users hold no
// write grants on that table, so the 7-day trial clock can never be reset
// from the client side. The stamp is best-effort here — a failed stamp
// must not fail the track — because getTrackerAccess (tracker-access.ts)
// lazily heals a missing row for any user who owns landscapes.
//
// Data access: watchlist reads/writes ride the user-scoped client + RLS
// (own-rows table); only the tracker_access stamp uses service-role.

import type { SupabaseClient } from "@supabase/supabase-js";
import {
  LANDSCAPE_COLUMNS,
  LandscapeDbError,
  LandscapeNotFoundError,
  LandscapeValidationError,
  type LandscapeRow,
} from "@/lib/pipeline-watch/landscapes";

export const WATCHLIST_NAME = "My tracked drugs";
/** Watchlists hold more drugs than comparisons (5) — tracking is one-at-a-time accretion, not a curated matchup. */
export const MAX_WATCHLIST_DRUGS = 10;

export class WatchlistCapError extends Error {
  constructor() {
    super(`Watchlist limit reached (${MAX_WATCHLIST_DRUGS} drugs).`);
    this.name = "WatchlistCapError";
  }
}

export type TrackDrugResult = {
  landscape: LandscapeRow;
  /** False when the drug was already on the watchlist (idempotent no-op). */
  added: boolean;
  /** True when this call created the watchlist row itself. */
  created: boolean;
  /** False when the best-effort tracker_access stamp failed (route logs it; getTrackerAccess self-heals). */
  trialStamped: boolean;
};

function normalizeDrugName(drug: unknown): string {
  const d = String(drug ?? "")
    .trim()
    .toLowerCase();
  if (!d) {
    throw new LandscapeValidationError("no_drugs", "Provide a drug to track.");
  }
  if (d.length > 100) {
    throw new LandscapeValidationError(
      "drug_name_invalid",
      "Drug names are limited to 100 characters.",
    );
  }
  return d;
}

async function findWatchlist(
  supabase: SupabaseClient,
): Promise<LandscapeRow | null> {
  const { data, error } = await supabase
    .from("watched_landscapes")
    .select(LANDSCAPE_COLUMNS)
    .eq("kind", "watchlist")
    .maybeSingle();
  if (error) throw new LandscapeDbError(error.message);
  return (data as LandscapeRow | null) ?? null;
}

/**
 * Load ONE watch by id, for the `landscapeId` path below.
 *
 * WHY THIS EXISTS. Both composers used to reach for `findWatchlist`, which
 * matches `kind='watchlist'` and nothing else, so on a MANDATE watch -- the
 * agent-created kind, whose identity is its name and its trial set -- an
 * untrack quietly did nothing and a track created a SECOND, disconnected
 * watchlist row the member never asked for. The dashboard's Manage watch panel
 * made that reachable, so the composers now take the id of the watch the
 * member is actually looking at.
 *
 * OWNERSHIP IS ASSERTED TWICE ON PURPOSE. RLS already restricts
 * `watched_landscapes` to own rows, so the `user_id` predicate is redundant
 * against a correctly configured database -- which is exactly why it is worth
 * having: it is the one line that keeps this correct if a policy is ever
 * loosened, and it costs an index lookup. A row that is not there and a row
 * that is not yours answer IDENTICALLY (both null -> NotFound), so this can
 * never be used to probe whether some other member's watch exists.
 */
async function findOwnedLandscape(
  supabase: SupabaseClient,
  landscapeId: string,
  userId: string,
): Promise<LandscapeRow | null> {
  const { data, error } = await supabase
    .from("watched_landscapes")
    .select(LANDSCAPE_COLUMNS)
    .eq("id", landscapeId)
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw new LandscapeDbError(error.message);
  return (data as LandscapeRow | null) ?? null;
}

/** Trim a caller-supplied landscape id to a usable string, or null. */
function normalizeLandscapeId(id: unknown): string | null {
  const v = typeof id === "string" ? id.trim() : "";
  return v.length > 0 ? v : null;
}

/**
 * MEMBERSHIP STATE THE SHARED COLUMN LIST DOES NOT CARRY.
 *
 * `LANDSCAPE_COLUMNS` is deliberately narrow and every read in the codebase
 * goes through it, so widening it to carry `scope_locked` and `drug_ncts`
 * would change the payload of a dozen unrelated composers. This is a second,
 * additive read of just the two columns the membership edits below need.
 *
 * IT THROWS RATHER THAN DEFAULTING. "Unknown lock state" must never be read as
 * "not locked": that is precisely how C1b's writes ended up decorative on a
 * scope_locked row.
 */
type LockState = { scopeLocked: boolean; drugNcts: Record<string, string[]> };

async function loadLockState(
  supabase: SupabaseClient,
  landscapeId: string,
): Promise<LockState> {
  const { data, error } = await supabase
    .from("watched_landscapes")
    .select("scope_locked, drug_ncts")
    .eq("id", landscapeId)
    .maybeSingle();
  if (error) throw new LandscapeDbError(error.message);
  if (!data) throw new LandscapeNotFoundError();
  return {
    scopeLocked: (data as { scope_locked?: boolean }).scope_locked === true,
    drugNcts:
      ((data as { drug_ncts?: Record<string, string[]> }).drug_ncts ?? {}) as Record<
        string,
        string[]
      >,
  };
}

/**
 * THE MANDATE'S OWN APPROVED TRIAL SET — the floor a member edit may never cut
 * below.
 *
 * WHY THE LEDGER AND NOT A NEW COLUMN. Three ways to know the base set were on
 * the table. Persisting `mandate_base_ncts` needs a migration, a backfill and a
 * new invariant to keep in step with every refresh. Re-deriving it means
 * re-running the mandate refresh — an agent pipeline with network and model
 * calls — inside a request path. But `mandate_trial_sets` ALREADY IS the
 * approved set: `refresh_mandate_membership` (0198) writes that table and
 * `watched_landscapes.watched_ncts` in ONE transaction precisely so the two
 * cannot disagree. Reading it is the smallest change by a wide margin, needs no
 * schema work, and is the same set the rule itself selected. Verified on the QA
 * watch: 470 ledger rows against 470 watched ncts, exactly equal.
 *
 * SERVICE CLIENT, AND IT MUST BE A REAL ONE. `monitoring_mandates` and
 * `mandate_trial_sets` are deny-all (measured: RLS on, zero policies, no SELECT
 * grant to `authenticated`), and supabase-js does NOT throw on 42501 — it
 * returns `{data: null, error}`. A caller that passed its own user client would
 * read an EMPTY base set and this function would then authorise removing every
 * approved trial. So an error here THROWS, and a landscape with no linked
 * mandate falls back to "everything currently watched is the floor", which
 * makes a removal subtract nothing rather than guess.
 */
async function loadMandateBaseNcts(
  service: SupabaseClient,
  landscapeId: string,
  currentNcts: readonly string[],
): Promise<Set<string>> {
  const { data: mandate, error: mErr } = await service
    .from("monitoring_mandates")
    .select("id")
    .eq("landscape_id", landscapeId)
    .maybeSingle();
  if (mErr) throw new LandscapeDbError(`mandate lookup failed: ${mErr.message}`);
  if (!mandate) {
    // A scope_locked landscape with no mandate behind it (the curated public
    // demo is one). Its pins are the product; nothing here may drop them.
    return new Set(currentNcts.map(String));
  }
  const mandateId = (mandate as { id: string }).id;

  // PAGED, AND ORDERED BY A UNIQUE COLUMN. PostgREST caps the RESPONSE at 1000
  // rows and `.limit()` does not lift it, so a 470-row set fits today and a
  // 1,200-row one would silently truncate -- which on this code path would
  // shrink the floor and authorise removing real trials. `.range()` without
  // `.order()` re-runs an unordered query per page, so pages overlap.
  const base = new Set<string>();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await service
      .from("mandate_trial_sets")
      .select("nct_id")
      .eq("mandate_id", mandateId)
      .order("nct_id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw new LandscapeDbError(`mandate base read failed: ${error.message}`);
    const rows = (data ?? []) as Array<{ nct_id: string }>;
    for (const r of rows) base.add(r.nct_id);
    if (rows.length < PAGE) break;
  }
  return base;
}

/** Case-insensitive index of a drug in a stored roster. -1 when absent. */
function rosterIndex(drugs: readonly string[], drug: string): number {
  const key = drug.trim().toLowerCase();
  return drugs.findIndex((d) => String(d).trim().toLowerCase() === key);
}

/** The stored bucket for one drug. Keys are LOWERCASED by the sweep's own
 *  convention while `drugs` carries display casing (migration 0206), so the
 *  lookup is case-insensitive in both directions. */
function bucketFor(
  drugNcts: Record<string, string[]>,
  drug: string,
): string[] {
  const key = drug.trim().toLowerCase();
  for (const [k, v] of Object.entries(drugNcts)) {
    if (k.trim().toLowerCase() === key) return (v ?? []).map(String);
  }
  return [];
}

/** Drop one drug's key from a bucket map, case-insensitively. */
function withoutBucket(
  drugNcts: Record<string, string[]>,
  drug: string,
): Record<string, string[]> {
  const key = drug.trim().toLowerCase();
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(drugNcts)) {
    if (k.trim().toLowerCase() !== key) out[k] = v;
  }
  return out;
}

/**
 * Stamp trial_started_at for this user if it has never been stamped.
 * ON CONFLICT DO NOTHING semantics — an existing row is untouched, so the
 * trial clock is immutable. Returns false (instead of throwing) on DB
 * error: the caller treats the stamp as best-effort.
 */
export async function stampTrialStart(
  service: SupabaseClient,
  userId: string,
): Promise<boolean> {
  const { error } = await service
    .from("tracker_access")
    .upsert({ user_id: userId }, { onConflict: "user_id", ignoreDuplicates: true });
  return !error;
}

export async function trackDrug(args: {
  /** User-scoped client (RLS) — all watchlist reads/writes. */
  supabase: SupabaseClient;
  /** Service-role client — tracker_access stamp only. */
  service: SupabaseClient;
  userId: string;
  drug: unknown;
  /**
   * Resolves one drug's full registry scope, for the scope_locked path below.
   *
   * INJECTED BY THE CALLER RATHER THAN IMPORTED HERE, and the reason is the
   * client bundle: three client components import `MAX_WATCHLIST_DRUGS` from
   * this file, so a static import of `fetch-watched` would drag the CTGov
   * fetchers into the browser. The route (server-only) hands in
   * `fetchDrugScopeNctIds`; offline tests hand in a stub.
   *
   * ABSENT ON A scope_locked ROW IS A HARD ERROR, never a silent skip -- a
   * skip is exactly the decorative append this phase removes.
   */
  resolveDrugScope?: (drug: string) => Promise<string[]>;
  /**
   * Add to THIS watch rather than to the V1 `kind='watchlist'` row.
   *
   * OPTIONAL, AND ABSENT MEANS THE OLD BEHAVIOUR EXACTLY. The marketing
   * `?track=` arrivals and any caller with no watch yet still get
   * find-or-create, which is the only path that can start a member's first
   * watch. Present, the id decides the row and the KIND IS IGNORED: a mandate
   * watch is a watch, and a member looking at one expects a drug they add to
   * land in the watch they can see.
   */
  landscapeId?: unknown;
}): Promise<TrackDrugResult> {
  const drug = normalizeDrugName(args.drug);
  const landscapeId = normalizeLandscapeId(args.landscapeId);

  // THE TARGETED PATH DOES NOT CREATE. An id that names no row the caller owns
  // is a 404, never a silent find-or-create somewhere else -- creating a watch
  // in answer to a request that named a different one is precisely the bug
  // this branch exists to remove.
  if (landscapeId) {
    const target = await findOwnedLandscape(args.supabase, landscapeId, args.userId);
    if (!target) throw new LandscapeNotFoundError();
    // CASE-INSENSITIVE, because mandate rows store DISPLAY casing (migration
    // 0206) while `drug` arrives lowercased. `includes(drug)` missed every
    // title-cased entry, so adding "Canagliflozin" to a watch that already had
    // it would have appended a second roster entry.
    if (rosterIndex(target.drugs, drug) >= 0) {
      const trialStamped = await stampTrialStart(args.service, args.userId);
      return { landscape: target, added: false, created: false, trialStamped };
    }
    if (target.drugs.length >= MAX_WATCHLIST_DRUGS) {
      throw new WatchlistCapError();
    }

    const lock = await loadLockState(args.supabase, target.id);
    const patch: Record<string, unknown> = { drugs: [...target.drugs, drug] };

    if (lock.scopeLocked) {
      /* THE JOBS THAT WOULD DO THIS ARE SKIPPED HERE, SO THE REQUEST DOES IT.
         bootstrap, sweep scope-drift and the watchlist reconcile all bail on a
         scope_locked row, which is why C1b's append was decorative: the drug
         joined the roster and not one trial joined the watch. This resolves the
         drug's registry scope and unions it into `watched_ncts` in the SAME
         update, so the roster and the watched set can never be seen apart.

         FAIL CLOSED. If the scope cannot be resolved the whole add fails and
         the member can retry. Appending anyway would write exactly the
         decorative row this phase exists to remove, and it would look like it
         had worked. The bound is CTGov's own: 3 pages of 1,000, which is the
         same cap the sweep uses. */
      if (!args.resolveDrugScope) {
        throw new LandscapeDbError(
          "scope_locked watch edited without a scope resolver",
        );
      }
      const scope = await args.resolveDrugScope(drug);
      const merged = [...target.watched_ncts];
      const seen = new Set(merged);
      for (const n of scope) {
        if (!seen.has(n)) {
          merged.push(n);
          seen.add(n);
        }
      }
      patch.watched_ncts = merged;
      // Lowercased key, matching the sweep's own convention for this column --
      // `loadMergedDrugByNct` normalises on the read side and would otherwise
      // miss the bucket. Stored as the scope, which after the union above is
      // exactly this drug's share of the pins (buildDrugNcts' definition).
      patch.drug_ncts = { ...lock.drugNcts, [drug]: scope };
    }

    const { data, error } = await args.supabase
      .from("watched_landscapes")
      .update(patch)
      .eq("id", target.id)
      .select(LANDSCAPE_COLUMNS)
      .single();
    if (error || !data) {
      throw new LandscapeDbError(error?.message ?? "watch update returned no row");
    }
    const trialStamped = await stampTrialStart(args.service, args.userId);
    return {
      landscape: data as LandscapeRow,
      added: true,
      created: false,
      trialStamped,
    };
  }

  let row = await findWatchlist(args.supabase);
  let created = false;
  let added = false;

  if (!row) {
    const { data, error } = await args.supabase
      .from("watched_landscapes")
      .insert({
        user_id: args.userId,
        name: WATCHLIST_NAME,
        drugs: [drug],
        watched_ncts: [],
        kind: "watchlist",
      })
      .select(LANDSCAPE_COLUMNS)
      .single();
    if (error) {
      // 23505 = a concurrent request won the one-watchlist-per-user index
      // race. Re-select the winner and fall through to the append path.
      if (error.code === "23505") {
        row = await findWatchlist(args.supabase);
        if (!row) throw new LandscapeDbError("watchlist race re-select found no row");
      } else {
        throw new LandscapeDbError(error.message);
      }
    } else if (data) {
      row = data as LandscapeRow;
      created = true;
      added = true;
    } else {
      throw new LandscapeDbError("watchlist insert returned no row");
    }
  }

  if (!created) {
    if (row.drugs.includes(drug)) {
      const trialStamped = await stampTrialStart(args.service, args.userId);
      return { landscape: row, added: false, created: false, trialStamped };
    }
    if (row.drugs.length >= MAX_WATCHLIST_DRUGS) {
      throw new WatchlistCapError();
    }
    const { data, error } = await args.supabase
      .from("watched_landscapes")
      .update({ drugs: [...row.drugs, drug] })
      .eq("id", row.id)
      .select(LANDSCAPE_COLUMNS)
      .single();
    if (error || !data) {
      throw new LandscapeDbError(error?.message ?? "watchlist update returned no row");
    }
    row = data as LandscapeRow;
    added = true;
  }

  const trialStamped = await stampTrialStart(args.service, args.userId);
  return { landscape: row, added, created, trialStamped };
}

export type UntrackDrugResult = {
  /** The watchlist after the removal; null when the user has no watchlist. */
  landscape: LandscapeRow | null;
  /** False when the drug wasn't on the watchlist (idempotent no-op). */
  removed: boolean;
};

/**
 * Remove one drug from the member's watchlist. Idempotent: unknown drugs
 * and missing watchlists return removed:false rather than erroring. When
 * the last drug goes, watched_ncts empties with it; otherwise the caller
 * enqueues a bootstrap job whose watchlist-reconcile mode recomputes
 * watched_ncts from the remaining drugs' registry scope.
 */
export class MandateLastDrugError extends Error {
  constructor() {
    super(
      "This watch needs at least one drug. Add another before removing this one.",
    );
    this.name = "MandateLastDrugError";
  }
}

export async function untrackDrug(args: {
  supabase: SupabaseClient;
  userId: string;
  drug: unknown;
  /** Remove from THIS watch — see `trackDrug`'s note. Absent = the V1 row. */
  landscapeId?: unknown;
  /**
   * Service-role client, for the mandate base-set read on a scope_locked row.
   *
   * DEFAULTS TO THE CALLER'S CLIENT and that default is a TRIPWIRE, not a
   * convenience: `mandate_trial_sets` is deny-all, so a user client makes the
   * read throw rather than quietly return an empty floor. The route passes the
   * real service client. Never default this to a real service client.
   */
  service?: SupabaseClient;
}): Promise<UntrackDrugResult> {
  const drug = normalizeDrugName(args.drug);
  const landscapeId = normalizeLandscapeId(args.landscapeId);
  const row = landscapeId
    ? await findOwnedLandscape(args.supabase, landscapeId, args.userId)
    : await findWatchlist(args.supabase);
  // A NAMED WATCH THAT IS NOT THERE IS A 404, not a no-op. Without the id the
  // absence of a V1 watchlist is a legitimate "nothing to remove from" and
  // still answers `removed:false`; WITH one, the caller asserted a specific
  // row exists, and answering 200/null is what let the client zero its own
  // roster on a mandate watch.
  if (landscapeId && !row) throw new LandscapeNotFoundError();
  // Case-insensitive on the NAMED path only, so the legacy path stays exactly
  // as it was. Mandate rosters carry display casing and `drug` is lowercased,
  // so the old `includes` silently no-opped on "Canagliflozin" and friends:
  // the row vanished from the panel for a moment and came back on reload.
  const present = row
    ? landscapeId
      ? rosterIndex(row.drugs, drug) >= 0
      : row.drugs.includes(drug)
    : false;
  if (!row || !present) {
    return { landscape: row, removed: false };
  }
  const nextDrugs = landscapeId
    ? row.drugs.filter((d) => String(d).trim().toLowerCase() !== drug)
    : row.drugs.filter((d) => d !== drug);

  const lock = landscapeId
    ? await loadLockState(args.supabase, row.id)
    : { scopeLocked: false, drugNcts: {} as Record<string, string[]> };

  let patch: Record<string, unknown>;
  if (lock.scopeLocked) {
    /* THE LAST-DRUG WIPE IS UNREACHABLE HERE, and that is the whole point of
       this branch. On a scope_locked mandate the wipe below would have set
       `watched_ncts = []` on a 470-trial approved set that NO job rebuilds --
       bootstrap, sweep-drift and reconcile all skip these rows -- so the set
       would have been gone for good. A watch with one drug left refuses
       instead, with copy that says what to do. */
    if (nextDrugs.length === 0) throw new MandateLastDrugError();

    /* SUBTRACT ONLY WHAT THIS DRUG ALONE BROUGHT. Three claims can keep an NCT
       in the watch and any one of them is enough:
         - the mandate's own approved base set (the floor -- see
           loadMandateBaseNcts; on the QA watch that is all 470, so removing a
           drug there changes the roster and not one watched trial), or
         - another remaining drug's bucket, or
         - nothing, in which case it leaves with the drug that brought it.
       An NCT with no stored bucket at all is never touched: absence of
       attribution is not evidence that this drug is why it is here. */
    const base = await loadMandateBaseNcts(
      args.service ?? args.supabase,
      row.id,
      row.watched_ncts,
    );
    const claimed = new Set<string>();
    for (const other of nextDrugs) {
      for (const n of bucketFor(lock.drugNcts, String(other))) claimed.add(n);
    }
    const removable = new Set(
      bucketFor(lock.drugNcts, drug).filter(
        (n) => !claimed.has(n) && !base.has(n),
      ),
    );
    patch = {
      drugs: nextDrugs,
      watched_ncts: row.watched_ncts.filter((n) => !removable.has(String(n))),
      drug_ncts: withoutBucket(lock.drugNcts, drug),
    };
  } else {
    patch =
      nextDrugs.length === 0
        ? { drugs: nextDrugs, watched_ncts: [] }
        : { drugs: nextDrugs };
  }
  const { data, error } = await args.supabase
    .from("watched_landscapes")
    .update(patch)
    .eq("id", row.id)
    .select(LANDSCAPE_COLUMNS)
    .single();
  if (error || !data) {
    throw new LandscapeDbError(error?.message ?? "watchlist update returned no row");
  }
  return { landscape: data as LandscapeRow, removed: true };
}

export const _internals = {
  normalizeDrugName,
  normalizeLandscapeId,
  findWatchlist,
  findOwnedLandscape,
};
